import { ShieldCheck } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { ConfirmButton } from '@/components/ConfirmButton'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { type OAuthAuthorization, scopeLabel } from '@/interfaces/OAuth'
import API from '@/lib/api'
import { formatDate } from '@/lib/formats/formatDate'

export const OAuthAuthorizations = ({
  authorizations,
  mutate,
}: {
  authorizations: OAuthAuthorization[]
  mutate: () => void
}) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'settings.api.oauthAuthorizations' })
  const { t: tr } = useTranslation()

  const [revokingId, setRevokingId] = useState<string | null>(null)

  const onRevoke = (clientId: string) =>
    API.delete(`/api/v2/settings/oauth-authorizations/${clientId}`)
      .then(() => {
        setRevokingId(null)
        mutate()
        toast.success(t('actions.revoke.success'))
      })
      .catch(() => {
        toast.error(t('actions.revoke.error'))
      })

  if (authorizations.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed px-6 py-10 text-center">
        <ShieldCheck className="size-5 text-muted-foreground" />
        <p className="text-sm font-medium">Žádné povolené aplikace</p>
        <p className="max-w-sm text-sm text-muted-foreground">
          Když povolíte aplikaci přístup k účtu, uvidíte ji tady a můžete jí přístup kdykoli odebrat.
        </p>
      </div>
    )
  }

  return (
    <div className="overflow-hidden rounded-lg border">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead>{t('table.name')}</TableHead>
            <TableHead className="hidden md:table-cell">{t('table.account')}</TableHead>
            <TableHead className="hidden md:table-cell">{t('table.scopes')}</TableHead>
            <TableHead className="hidden w-32 md:table-cell">{t('table.authorizedAt')}</TableHead>
            <TableHead className="w-px" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {authorizations.map((authorization) => {
            // One app can have a row for each account, so the client ID alone is not unique.
            const rowKey = `${authorization.client.id}:${authorization.account.id}`

            return (
            <TableRow key={rowKey}>
              <TableCell className="font-medium">{authorization.client.name}</TableCell>
              <TableCell className="hidden text-muted-foreground md:table-cell">{authorization.account.name}</TableCell>
              <TableCell className="hidden text-muted-foreground md:table-cell">
                {authorization.scopes.map((scope) => <div key={scope}>{scopeLabel(tr, scope)}</div>)}
              </TableCell>
              <TableCell className="hidden text-muted-foreground tabular md:table-cell">
                {formatDate(authorization.authorizedAt)}
              </TableCell>
              <TableCell>
                <ConfirmButton
                  title={t('actions.revoke.title')}
                  description={t('actions.revoke.description')}
                  confirmLabel={t('actions.revoke.confirm')}
                  open={revokingId === rowKey}
                  onOpenChange={(open: boolean) => !open && setRevokingId(null)}
                  onConfirm={() => onRevoke(authorization.client.id)}
                >
                  <Button variant="outline" size="sm" onClick={() => setRevokingId(rowKey)}>
                    {t('actions.revoke.title')}
                  </Button>
                </ConfirmButton>
              </TableCell>
            </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </div>
  )
}
